import { ApiError, apiRequest } from '@/lib/queryClient';
import type { GenerateSummaryRequest, GenerateSummaryResponse } from '@/types/summaryGeneration';
import { useMutation } from '@tanstack/react-query';

const generateSummary = async (request: GenerateSummaryRequest): Promise<GenerateSummaryResponse> => {
  let response: Response;
  try {
    response = await apiRequest('POST', '/api/summary/generate', request);
  } catch (error) {
    if (error instanceof ApiError) {
      const message =
        error.status === 401
          ? 'Authentication required'
          : error.status === 403
            ? 'Insufficient permissions'
            : 'Failed to generate summary';
      throw new ApiError(error.status, message);
    }
    throw error;
  }
  const data: Partial<GenerateSummaryResponse> = await response.json();
  if (!data || data.status !== 'success' || typeof data.soap_note !== 'string' || !data.soap_note) {
    throw new Error('Failed to generate summary');
  }
  return data as GenerateSummaryResponse;
};

export const useGenerateSummary = () =>
  useMutation({
    mutationFn: generateSummary,
    // Generation persists a new note and must never retry automatically.
    retry: false,
    // Preserve fetch semantics: an offline click must not queue a future note.
    networkMode: 'always',
    // Release transcript/result data as soon as the mutation is inactive.
    gcTime: 0,
  });
